/**
 * Schrijft bij het bouwen een klein bestandje met de naam van het scriptbestand dat de startpagina
 * laadt.
 *
 * Die naam is het versiekenmerk. Vite geeft hem een hash die de inhoud volgt: dezelfde code levert
 * dezelfde naam op, gewijzigde code een andere. Daarom staat hier met opzet géén tijdstempel en
 * géén met de hand bijgehouden nummer — het eerste meldt een nieuwe versie na elke herbouw van
 * identieke code, het tweede staat binnen een maand verkeerd.
 */
import type { Plugin } from 'vite';

export const VERSIEBESTAND_NAAM = 'version.json';

/** Het pad waarop het scherm het bestandje opvraagt. Server en scherm delen deze ene waarde. */
export const VERSIEBESTAND_PAD = `/${VERSIEBESTAND_NAAM}`;

type Brok = { type: string; isEntry?: boolean; name?: string; fileName: string };

export function versiebestand(): Plugin {
  // De grondmap waaronder het portaal wordt uitgeleverd. Staat die op iets anders dan `/`, dan
  // draagt de scripttag in de startpagina die map wél en de bestandsnaam uit de uitlevering niet —
  // en dan zouden de twee kanten nooit gelijk zijn en stond de melding er permanent.
  let grondmap = '/';

  return {
    name: 'xxtract-versiebestand',
    apply: 'build',

    configResolved(config) {
      grondmap = config.base;
    },

    generateBundle(_opties, bundel) {
      const ingangen = Object.values(bundel as Record<string, Brok>)
        .filter((brok) => brok.type === 'chunk' && brok.isEntry === true);

      // Bij meerdere ingangen is de volgorde van de uitlevering niet gekoppeld aan de
      // startpagina; die heet bij Vite `index`. Zonder deze keuze zou een tweede ingang een
      // verschil opleveren dat nooit meer weggaat.
      const ingang = ingangen.find((brok) => brok.name === 'index') ?? ingangen[0];

      if (ingang === undefined) {
        // Zonder waarschuwing verdwijnt de hele versiemelding geruisloos: het bestandje ontbreekt,
        // de server antwoordt "bestaat niet", en het scherm zwijgt daar netjes over.
        this.warn(
          `Geen ingang gevonden in de uitlevering; ${VERSIEBESTAND_NAAM} is niet geschreven `
          + 'en de versiemelding blijft daardoor uit.',
        );
        return;
      }

      if (ingangen.length > 1 && ingang.name !== 'index') {
        this.warn(
          `Meerdere ingangen gevonden en geen enkele heet "index"; ${VERSIEBESTAND_NAAM} wijst nu `
          + `naar ${ingang.fileName}. Klopt dat niet met de startpagina, dan blijft de `
          + 'versiemelding staan.',
        );
      }

      const voorvoegsel = grondmap.endsWith('/') ? grondmap : `${grondmap}/`;

      this.emitFile({
        type: 'asset',
        fileName: VERSIEBESTAND_NAAM,
        source: JSON.stringify({ entry: `${voorvoegsel}${ingang.fileName}` }),
      });
    },
  };
}
